import { c as _c } from "react/compiler-runtime";
import strikeRulesFactory, { type PriceStrikeRules, type StrikePriceInfo } from './price-strike-rules';
import { CapacityUnitCode } from '@spa/lib/converters/convertCapacityUnitCode';
import { CurrencyIso } from '@spa/lib/converters/convertCurrencyIso';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { ReactElement } from 'react';
import type { StateCartPrice } from '@spa/lib/store/carts/CartsState';
import calculatePriceValues from '../../lib/getPriceValues';
import classNames from 'clsx';
import formatPriceWithCurrency from '../../lib/formatPriceWithCurrency';
import styles from './product-price.component.module.scss';
import useFormattedDiscount from '../../lib/react/hooks/useFormattedDiscount';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export interface BasePrice {
  readonly capacity: Capacity;
  readonly value: number;
}
export interface Capacity {
  readonly unitCode: CapacityUnitCode;
  readonly value: string;
}
export enum ProductPriceType {
  TILE = 'TILE',
  INFO = 'INFO',
}
export interface Props {
  readonly basePrice: BasePrice | null;
  readonly capacity?: Capacity | null;
  readonly currentPrice: StateCartPrice;
  readonly recommendedRetailPrice: StateCartPrice | null;
  readonly campaignPrice: StateCartPrice | null;
  readonly strikePrice: StateCartPrice | null;
  readonly lowestVariantPrice?: StateCartPrice | null;
  readonly pricesIncludeTax: boolean;
  readonly locale: string;
  readonly country: string;
  readonly type?: ProductPriceType;
  readonly compactMode?: boolean;
  readonly reserveStrikePriceSpace?: boolean;
  readonly reserveBasePriceSpace?: boolean;
  readonly dataTestIdContext?: string;
}
const ProductPrice = t0 => {
  const $ = _c(26);
  const {
    basePrice,
    capacity,
    currentPrice,
    recommendedRetailPrice,
    campaignPrice,
    strikePrice,
    lowestVariantPrice,
    pricesIncludeTax,
    locale,
    country,
    type: t1,
    compactMode,
    reserveStrikePriceSpace,
    reserveBasePriceSpace,
    dataTestIdContext
  } = t0;
  const type = t1 === undefined ? ProductPriceType.INFO : t1;
  const {
    formatMessage
  } = useNamespacedIntl("product-price");
  const {
    formatMessage: capacityUnitsFormatMessage
  } = useNamespacedIntl("capacity-units");
  const {
    currentPriceValue,
    recommendedRetailPriceValue,
    campaignPriceValue,
    strikePriceValue,
    lowestVariantPriceValue
  } = calculatePriceValues(pricesIncludeTax, {
    currentPrice,
    recommendedRetailPrice,
    campaignPrice,
    strikePrice,
    lowestVariantPrice: type === ProductPriceType.TILE ? lowestVariantPrice : null
  });
  const strikeRules = strikeRulesFactory(country, {
    currentPriceValue,
    recommendedRetailPriceValue,
    campaignPriceValue,
    strikePriceValue
  });
  const currencyIso = currentPrice.currencyIso;
  const resolvedStrikePriceInfo = strikeRules.findStrikePriceInfo();
  const strikePriceInfo = !lowestVariantPriceValue ? resolvedStrikePriceInfo : undefined;
  const mainPriceValue = typeof campaignPriceValue !== "undefined" ? campaignPriceValue : currentPriceValue;
  const formattedDiscount = useFormattedDiscount(strikePriceInfo?.priceValue, mainPriceValue, locale);
  const displayedPriceValue = lowestVariantPriceValue ?? mainPriceValue;
  const typeClassName = `product-price--${type.toLowerCase()}`;
  const currentPriceTestId = dataTestIdContext ? `${dataTestIdContext}-currentPrice-label` : undefined;
  const strikePriceTestId = dataTestIdContext ? `${dataTestIdContext}-strikePrice-label` : undefined;
  const savingsTestId = dataTestIdContext ? `${dataTestIdContext}-savings-label` : undefined;
  const priceBlockClassnames = classNames({
    [styles["product-price"]]: true,
    [styles[typeClassName]]: true,
    [styles["product-price--compact"]]: compactMode === true,
    [styles["product-price--reserve-strike-space"]]: reserveStrikePriceSpace && strikePriceInfo === undefined,
    [styles["product-price--reserve-base-space"]]: reserveBasePriceSpace && (basePrice && lowestVariantPriceValue || basePrice === null)
  });
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classNames(styles["price-group"]);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = type === ProductPriceType.INFO && styles["product-price__main-price"];
  const t4 = type === ProductPriceType.TILE && styles["product-price__main-price--tile"];
  const t5 = compactMode && "bold";
  const t6 = strikePriceInfo ? styles["product-price__main-price-accent"] : styles["product-price__main-price"];
  let t7;
  if ($[1] !== t3 || $[2] !== t4 || $[3] !== t5 || $[4] !== t6) {
    t7 = classNames(t3, t4, t5, t6);
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classNames(styles["product-price__base-price"], "text-lg-immutable ");
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== basePrice || $[8] !== capacityUnitsFormatMessage || $[9] !== currencyIso || $[10] !== formatMessage || $[11] !== locale || $[12] !== lowestVariantPriceValue) {
    t9 = basePrice && !lowestVariantPriceValue && <span>{formatMessage("basePrice", {
        price: `${formatPriceWithCurrency(basePrice.value, currencyIso, locale)}`,
        capacity: `${basePrice.capacity.value} ${capacityUnitsFormatMessage(basePrice.capacity.unitCode)}`
      })}</span>;
    $[7] = basePrice;
    $[8] = capacityUnitsFormatMessage;
    $[9] = currencyIso;
    $[10] = formatMessage;
    $[11] = locale;
    $[12] = lowestVariantPriceValue;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== formatMessage || $[15] !== pricesIncludeTax || $[16] !== type) {
    t10 = type === ProductPriceType.INFO && pricesIncludeTax && <span>{formatMessage("withTaxes")}</span>;
    $[14] = formatMessage;
    $[15] = pricesIncludeTax;
    $[16] = type;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div className={t8}>{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== formatMessage || $[22] !== formattedDiscount || $[23] !== savingsTestId || $[24] !== type) {
    t12 = type === ProductPriceType.INFO && formattedDiscount && <span className={classNames(styles["product-price__price-discount"], "text-lg-immutable ")} data-testid={savingsTestId}>{formatMessage("discountText", {
        discount: formattedDiscount
      })}</span>;
    $[21] = formatMessage;
    $[22] = formattedDiscount;
    $[23] = savingsTestId;
    $[24] = type;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return <div className={priceBlockClassnames}><div className={styles["product-price__price-wrapper"]}><div className={t2}>{!!lowestVariantPriceValue && <span className={styles["product-price__from"]}>{formatMessage("from")} </span>}<span className={t7} data-track-id="priceContainer" data-testid={currentPriceTestId}>{formatPriceWithCurrency(displayedPriceValue, currencyIso, locale)}</span>{type === ProductPriceType.TILE && capacity && !lowestVariantPriceValue && <span className={styles["product-price__capacity"]}> / {capacity.value} {capacityUnitsFormatMessage(capacity.unitCode)}</span>}</div>{strikePriceInfo && !lowestVariantPriceValue && <div className={classNames(type === ProductPriceType.INFO && "text-lg-immutable ", styles["product-price__strike-price"])}>{strikePriceInfo.isUVP && <span className={styles["product-price__strike-uvp"]}>{formatMessage("recommendedRetailPrice")}</span>}<span className={classNames(styles["product-price__strike-price-value"])} data-testid={strikePriceTestId}>{formatPriceWithCurrency(strikePriceInfo.priceValue, currencyIso, locale)}</span></div>}</div>{t11}{t12}</div>;
};
export default ProductPrice;